import { useState } from 'react';
import { useSearchParams, Navigate, useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';

export function Login() {
  const { isAuthenticated, loading, loginWithEmail } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlError = searchParams.get('error');
  const returnTo = searchParams.get('return_to');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-0">
        <div className="h-8 w-8 spinner-gradient" />
      </div>
    );
  }

  if (isAuthenticated) {
    // If there's a return_to param (e.g., from MCP OAuth flow), redirect there
    if (returnTo) {
      window.location.href = returnTo;
      return null;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const error = formError || urlError;

  async function handleEmailLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    setFormError('');
    setSubmitting(true);
    try {
      await loginWithEmail(email, password);
      // If there's a return_to param (e.g., from MCP OAuth flow), redirect there
      if (returnTo) {
        window.location.href = returnTo;
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-0">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center">
            <img src="/logo.png" alt="Palaryn" width="48" height="48" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-text-primary">
            Sign in to Palaryn
          </h1>
          <p className="mt-2 text-sm text-text-secondary">
            Secure your AI agent tool calls
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] px-4 py-3 text-sm text-[var(--color-error)]">
            {error}
          </div>
        )}

        <div className="rounded-xl border border-border-dark bg-surface-1 p-6 shadow-sm">
          {/* Email/Password form */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-primary">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 block w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:ring-1 focus:ring-accent-teal focus:outline-none"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-primary">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 block w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:ring-1 focus:ring-accent-teal focus:outline-none"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-gradient-to-r from-accent-teal to-accent-violet px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

        </div>

        {import.meta.env.DEV && (
          <p className="mt-4 text-center text-xs text-text-tertiary">
            Dev mode: create test accounts via seed script (npm run seed)
          </p>
        )}
      </div>
    </div>
  );
}
